import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatNotifyPrefs } from '@/lib/chat/chat.notify.prefs';
import { usePushCalendar } from '@/lib/push/push.calendar.prefs';
import { pushCurrent, pushDeviceLabel, pushFingerprint, pushKeys, pushMarkActive, pushOptedOut, pushSubscribe, pushSupport } from '@/lib/push/push.client';
import { selectSessionUser } from '@/store/auth/auth.selectors';
import { useReadPushQuery, useRemovePushDeviceMutation, useSavePushDeviceMutation } from '@/store/api/notification/push.api';
import { useAppSelector } from '@/store/store.hooks';
import { useNotificationPermission } from '@/hooks/use.notification.permission';

export function usePushBridge() {
    const navigate = useNavigate();
    const prefs = useChatNotifyPrefs();
    const calendar = usePushCalendar();
    const signedIn = Boolean(useAppSelector(selectSessionUser));
    const supported = React.useMemo(() => pushSupport().supported, []);
    const state = useReadPushQuery(undefined, { skip: !signedIn || !supported });
    const [save] = useSavePushDeviceMutation();
    const [remove] = useRemovePushDeviceMutation();
    const permission = useNotificationPermission();

    React.useEffect(() => {
        if (!supported) return undefined;
        const onMessage = (event: MessageEvent) => {
            const data = event.data as { type?: unknown; url?: unknown } | null;
            if (data?.type !== 'bd-navigate' || typeof data.url !== 'string' || !data.url.startsWith('/') || data.url.startsWith('//')) return;
            navigate(data.url);
        };
        navigator.serviceWorker.addEventListener('message', onMessage);
        return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    }, [navigate, supported]);

    const publicKey = state.data?.enabled ? state.data.publicKey : '';
    const devices = state.data?.devices;

    React.useEffect(() => {
        if (!signedIn || !supported || permission === 'granted' || !devices) return;
        void (async () => {
            const subscription = await pushCurrent();
            pushMarkActive(false);
            if (!subscription) return;
            const fingerprint = await pushFingerprint(subscription.endpoint);
            const registered = devices.find((device) => device.fingerprint === fingerprint);
            if (registered) await remove(registered.id).unwrap().catch(() => undefined);
            await subscription.unsubscribe().catch(() => false);
        })();
    }, [signedIn, supported, permission, devices, remove]);

    React.useEffect(() => {
        if (!signedIn || !supported || !publicKey || pushOptedOut()) return;
        if (permission !== 'granted') return;
        void (async () => {
            const subscription = (await pushCurrent()) ?? await pushSubscribe(publicKey).catch(() => null);
            const keys = subscription && subscription !== 'denied' ? pushKeys(subscription) : null;
            if (!keys) return;
            try {
                await save({ ...keys, ...pushDeviceLabel(), level: prefs.level, preview: prefs.preview, calendar }).unwrap();
                pushMarkActive(true);
            } catch {
                return;
            }
        })();
    }, [signedIn, supported, publicKey, permission, prefs.level, prefs.preview, calendar, save]);
}

export default usePushBridge;
